import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 30000,
  headers: { "Content-Type": "application/json" }
});

const initialState = {
  items: [],
  proposalToEdit: null,
  fetchStatus: "idle",
  createStatus: "idle",
  updateStatus: "idle",
  deleteStatus: "idle",
  error: null
};

export const selectAllProposals = state => state.proposals.items;

export const selectProposalById = (state, proposalId) =>
  state.proposals.items.find(proposal => proposal.id === proposalId);

export const fetchProposals = createAsyncThunk("proposals/fetch", async () => {
  const response = await instance.get("/proposals");
  return response.data.proposals;
});

export const setProposalToEdit = createAsyncThunk(
  "proposals/setProposalToEdit",
  async (id, { getState }) => {
    const state = getState();
    const proposal = state.proposals.items.find(i => i.id.toString() === id);
    if (proposal) return proposal;
    const response = await instance.get(`/proposals/${id}`);
    return response.data;
  }
);

export const createProposal = createAsyncThunk(
  "proposals/create",
  async proposal => {
    const response = await instance.post("/proposals", proposal);
    return response.data;
  }
);

export const updateProposal = createAsyncThunk(
  "proposals/update",
  async proposal => {
    const id = proposal.id;
    delete proposal.id;
    const response = await instance.put(`/proposals/${id}`, proposal);
    return response.data;
  }
);

export const deleteProposal = createAsyncThunk(
  "proposals/delete",
  async proposal => {
    const id = proposal.id;
    delete proposal.id;
    await instance.delete(`/proposals/${id}`);
    return proposal;
  }
);

export const proposalsSlice = createSlice({
  name: "proposals",
  initialState,
  reducers: {
    resetProposalDeleteStatus(state) {
      state.deleteStatus = "idle";
    }
  },
  extraReducers: {
    [fetchProposals.pending]: state => {
      state.fetchStatus = "loading";
    },
    [fetchProposals.fulfilled]: (state, action) => {
      state.fetchStatus = "succeeded";
      state.items = state.items = action.payload;
    },
    [fetchProposals.rejected]: (state, action) => {
      state.fetchStatus = "failed";
      state.error = action.error.message;
    },
    [createProposal.pending]: state => {
      state.createStatus = "loading";
    },
    [createProposal.fulfilled]: (state, action) => {
      state.createStatus = "succeeded";
      const newItems = [...state.items];
      newItems.unshift(action.payload);
      state.items = newItems;
    },
    [createProposal.rejected]: (state, action) => {
      state.createStatus = "failed";
      state.error = action.error.message;
    },
    [setProposalToEdit.fulfilled]: (state, action) => {
      state.proposalToEdit = action.payload;
    },
    [updateProposal.pending]: state => {
      state.updateStatus = "loading";
    },
    [updateProposal.fulfilled]: (state, action) => {
      state.updateStatus = "succeeded";
      state.items = state.items.map(item =>
        action.payload.id === item.id ? action.payload : item
      );
      state.proposalToEdit = action.payload;
    },
    [updateProposal.rejected]: (state, action) => {
      state.updateStatus = "failed";
      state.error = action.error.message;
    },
    [deleteProposal.pending]: state => {
      state.deleteStatus = "loading";
    },
    [deleteProposal.fulfilled]: (state, action) => {
      state.deleteStatus = "succeeded";
      let newItems = [...state.items];
      let indexToRemove = newItems
        .map(newItem => newItem.id)
        .indexOf(action.payload.id);
      newItems.splice(indexToRemove, 1);
      state.items = newItems;
    },
    [deleteProposal.rejected]: (state, action) => {
      state.deleteStatus = "failed";
      state.error = action.error.message;
    }
  }
});

const { reducer } = proposalsSlice;
export const { resetProposalDeleteStatus } = proposalsSlice.actions;
export default reducer;
